/**
 * planPdf.ts — Frontend utility to trigger a PDF download from the Worker.
 *
 * Design: ADR-1 / design §1.5 / plan-pdf-export spec.
 *
 * Usage:
 *   await downloadPlanPdf({ workerUrl, getIdToken });
 *   await downloadPlanPdf({ workerUrl, getIdToken, planId: "plan-123" });
 *
 * Security:
 *   - Authorization: Bearer <idToken> — always fresh from getIdToken().
 *   - No plan data is sent; the Worker reads it from Firestore via SA.
 *   - PDF bytes are consumed as a Blob and released via URL.revokeObjectURL.
 *
 * Error handling:
 *   - Network errors → PdfDownloadError (status 0).
 *   - Non-200 responses → PdfDownloadError with HTTP status.
 *   - No raw server bodies surfaced to the user (no PII leak).
 *
 * Req: plan-pdf-export §1, design §1.5
 */

// ---------------------------------------------------------------------------
// Typed error
// ---------------------------------------------------------------------------

export class PdfDownloadError extends Error {
  override name = "PdfDownloadError";
  readonly status: number;
  constructor(message: string, status = 0) {
    super(message);
    this.status = status;
  }
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

export interface DownloadPlanPdfOptions {
  /** Worker base URL (e.g. https://autodieta-worker.workers.dev). */
  workerUrl: string;
  /** Returns a fresh Firebase ID token. Called once per download. */
  getIdToken: () => Promise<string>;
  /**
   * Specific plan document ID. When absent, the Worker resolves the active plan.
   * This value appears in the URL path scoped to the caller's uid namespace.
   */
  planId?: string;
  /** Injectable fetch for tests. Defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

// ---------------------------------------------------------------------------
// Main function
// ---------------------------------------------------------------------------

/**
 * Download the user's meal plan as a PDF file.
 *
 * Calls GET /me/plan/pdf (or /me/plan/:planId/pdf) with a Bearer token.
 * On success, triggers a browser download via a temporary <a> element.
 *
 * @throws PdfDownloadError on network failure or non-200 response.
 */
export async function downloadPlanPdf(opts: DownloadPlanPdfOptions): Promise<void> {
  const { workerUrl, getIdToken, planId } = opts;
  const fetchImpl = opts.fetchImpl ?? fetch;

  const base = workerUrl.replace(/\/+$/, "");
  const path = planId !== undefined ? `/me/plan/${planId}/pdf` : "/me/plan/pdf";
  const url = `${base}${path}`;

  // 1. Get a fresh ID token.
  let idToken: string;
  try {
    idToken = await getIdToken();
  } catch {
    throw new PdfDownloadError("Failed to obtain authentication token.", 0);
  }

  // 2. Fetch the PDF.
  let res: Response;
  try {
    res = await fetchImpl(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${idToken}`,
      },
    });
  } catch {
    throw new PdfDownloadError("Network error while downloading PDF.", 0);
  }

  if (!res.ok) {
    throw new PdfDownloadError("PDF download failed.", res.status);
  }

  // 3. Convert to Blob and trigger download.
  let blob: Blob;
  try {
    blob = await res.blob();
  } catch {
    throw new PdfDownloadError("Failed to read PDF response.", 0);
  }

  // Extract filename from Content-Disposition header if present.
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const filenameMatch = /filename="([^"]+)"/.exec(disposition);
  const filename = filenameMatch?.[1] ?? "autodieta-plan.pdf";

  // 4. Trigger browser download via temporary <a> element.
  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    // Always revoke the object URL to release memory.
    URL.revokeObjectURL(objectUrl);
  }
}
